import { useState, type ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { useTaskDetail, useCompleteTask, useSkipTask, useDeleteTask } from '../../hooks/useTasks';
import { useSuggestionReasoning } from '../../hooks/useAiSuggestions';
import { useTranslation } from '../../i18n/useTranslation';
import { isDosingPlanTask, type RecurrenceScope, type Task } from '../../types/task';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../ui/Dialog/Dialog';
import { DeleteTaskDialog, EditScopeDialog } from './RecurrenceScope';
import { useTaskFormat } from './taskFormat';
import './TaskDetail.css';

interface TaskDetailProps {
  /** Task id or occurrence id (`<seriesId>_<YYYY-MM-DD>`). */
  taskId: string;
  onClose: () => void;
  /** Edit the task; for a recurring task with the chosen scope. */
  onEdit?: (task: Task, scope: RecurrenceScope | undefined) => void;
  onDeleted?: () => void;
}

interface ReasoningView {
  explanation?: unknown;
  evidence?: Array<{ id: string; label: string }>;
  confidence?: number;
}

function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'completed': return 'success';
    case 'in_progress': return 'info';
    case 'skipped':
    case 'archived': return 'neutral';
    default: return 'neutral';
  }
}

function priorityTone(priority: string): BadgeTone {
  switch (priority) {
    case 'critical':
    case 'high': return 'danger';
    case 'medium': return 'warning';
    default: return 'info';
  }
}

type ActionError = 'complete' | 'skip' | 'delete' | null;

/** Link to the aquarium's dosing page (a plain link outside a router, e.g. in isolated tests). */
function DosingPageLink({ aquariumId, children }: { aquariumId: string; children: ReactNode }) {
  const inRouter = useInRouterContext();
  const to = `/aquariums/${aquariumId}/dosing`;
  return inRouter ? (
    <Link to={to} className="task-detail__link">{children}</Link>
  ) : (
    <a href={to} className="task-detail__link">{children}</a>
  );
}

export function isRecurringTask(task: Pick<Task, 'isRecurring' | 'recurrenceRule'>): boolean {
  return task.isRecurring ?? Boolean(task.recurrenceRule);
}

export function TaskDetail({ taskId, onClose, onEdit, onDeleted }: TaskDetailProps) {
  const { t } = useTranslation();
  const fmt = useTaskFormat();
  const { task, isLoading, isError, refetch } = useTaskDetail(taskId);

  const { mutate: completeTaskMutate, isPending: isCompleting } = useCompleteTask();
  const { mutate: skipTaskMutate, isPending: isSkipping } = useSkipTask();
  const { mutate: deleteTaskMutate, isPending: isDeleting } = useDeleteTask();

  const reasoningQuery = useSuggestionReasoning({
    suggestionId: task?.aiSuggestionId ?? '',
    enabled: Boolean(task?.isAiGenerated && task?.aiSuggestionId),
  });
  const reasoning = (reasoningQuery.reasoning ?? null) as ReasoningView | null;

  const [actionError, setActionError] = useState<ActionError>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editScopeOpen, setEditScopeOpen] = useState(false);

  const recurring = task ? isRecurringTask(task) : false;
  // Generated from a manual dosing plan: changed, paused or deleted on the dosing page.
  const fromDosingPlan = task ? isDosingPlanTask(task) : false;
  const isActionLoading = isCompleting || isSkipping || isDeleting;
  const isTaskActive = task?.status === 'open' || task?.status === 'in_progress';

  function handleComplete() {
    if (!task) return;
    setActionError(null);
    completeTaskMutate({ taskId: task.id }, { onError: () => setActionError('complete') });
  }

  function handleSkip() {
    if (!task) return;
    setActionError(null);
    skipTaskMutate({ taskId: task.id }, { onError: () => setActionError('skip') });
  }

  function handleDelete(scope: RecurrenceScope | undefined) {
    if (!task) return;
    setActionError(null);
    deleteTaskMutate(scope ? { taskId: task.id, scope } : task.id, {
      onSuccess: () => {
        setDeleteOpen(false);
        onDeleted?.();
        onClose();
      },
      onError: () => {
        setDeleteOpen(false);
        setActionError('delete');
      },
    });
  }

  function handleEdit() {
    if (!task || !onEdit) return;
    if (recurring) setEditScopeOpen(true);
    else onEdit(task, undefined);
  }

  const due = task ? fmt.due(task) : null;

  const footer = task && !isLoading && !isError ? (
    <div className="task-detail__footer">
      {isTaskActive && (
        <Button variant="primary" onClick={handleComplete} disabled={isActionLoading} isLoading={isCompleting}>
          {t('tasks.detail.complete')}
        </Button>
      )}
      {isTaskActive && (
        <Button variant="secondary" onClick={handleSkip} disabled={isActionLoading} isLoading={isSkipping}>
          {t('tasks.detail.skip')}
        </Button>
      )}
      {onEdit && !fromDosingPlan && (
        <Button variant="secondary" onClick={handleEdit} disabled={isActionLoading}>
          {t('tasks.detail.edit')}
        </Button>
      )}
      {!fromDosingPlan && (
        <Button variant="danger" onClick={() => setDeleteOpen(true)} disabled={isActionLoading}>
          {t('tasks.detail.delete')}
        </Button>
      )}
    </div>
  ) : undefined;

  return (
    <>
      <Dialog
        isOpen
        onClose={onClose}
        title={task?.title ?? t('tasks.detail.fallbackTitle')}
        footer={footer}
        data-testid="task-detail"
      >
        {isLoading && (
          <LiveRegion busy className="task-detail__loading" data-testid="task-detail-loading">
            {t('tasks.detail.loading')}
          </LiveRegion>
        )}

        {isError && !isLoading && (
          <LoadErrorState title={t('tasks.detail.loadError')} onRetry={() => void refetch()} compact />
        )}

        {!isLoading && !isError && task && (
          <div className="task-detail__body">
            <div className="task-detail__badges">
              <Badge tone={statusTone(task.status)} data-testid="task-detail-status">{fmt.status(task.status)}</Badge>
              <Badge tone={priorityTone(task.priority)} data-testid="task-detail-priority">{fmt.priority(task.priority)}</Badge>
              {recurring && <Badge tone="info">{t('tasks.recurrence.badge')}</Badge>}
              {task.isAiGenerated && <Badge tone="ai">{t('tasks.detail.aiGenerated')}</Badge>}
              {fromDosingPlan && <Badge tone="info" data-testid="task-detail-dosing-badge">{t('tasks.dosing.badge')}</Badge>}
            </div>

            {fromDosingPlan && (
              <Alert
                tone="info"
                live="off"
                compact
                data-testid="task-detail-dosing-note"
                action={<DosingPageLink aquariumId={task.aquariumId}>{t('tasks.dosing.openPlan')}</DosingPageLink>}
              >
                {t('tasks.dosing.managedNote')}
              </Alert>
            )}

            {actionError && (
              <Alert compact onDismiss={() => setActionError(null)} dismissLabel={t('common.close')}>
                {t(`tasks.detail.${actionError}Error`)}
              </Alert>
            )}

            {recurring && task.isDetached && (
              <Text size="sm" color="muted">{t('tasks.detail.detachedNote')}</Text>
            )}

            <section className="task-detail__section" aria-labelledby="task-detail-overview">
              <Heading level={3} id="task-detail-overview" className="task-detail__section-title">
                {t('tasks.detail.overview')}
              </Heading>
              <dl className="task-detail__fields">
                <dt className="task-detail__field-label">{t('tasks.detail.due')}</dt>
                <dd className="task-detail__field-value" data-testid="task-detail-due">
                  {due ? <time dateTime={task.startsAt ?? task.dueDate ?? undefined}>{due}</time> : t('tasks.detail.noDueDate')}
                </dd>

                {!task.isAiGenerated && (
                  <>
                    <dt className="task-detail__field-label">{t('tasks.detail.category')}</dt>
                    <dd className="task-detail__field-value" data-testid="task-detail-category">{fmt.category(task.category)}</dd>
                  </>
                )}

                {fromDosingPlan && fmt.dose(task) && (
                  <>
                    <dt className="task-detail__field-label">{t('tasks.dosing.dose')}</dt>
                    <dd className="task-detail__field-value" data-testid="task-detail-dose">{fmt.dose(task)}</dd>
                  </>
                )}

                {task.recurrenceRule && (
                  <>
                    <dt className="task-detail__field-label">{t('tasks.detail.recurrence')}</dt>
                    <dd className="task-detail__field-value" data-testid="task-detail-recurrence">{fmt.recurrence(task.recurrenceRule)}</dd>
                  </>
                )}

                <dt className="task-detail__field-label">{t('tasks.detail.emailReminder')}</dt>
                <dd className="task-detail__field-value" data-testid="task-detail-email-reminder">
                  {task.emailReminder ? t('tasks.detail.emailReminderOn') : t('tasks.detail.emailReminderOff')}
                </dd>

                {task.description && (
                  <>
                    <dt className="task-detail__field-label">{t('tasks.detail.description')}</dt>
                    <dd className="task-detail__field-value task-detail__field-value--description">{task.description}</dd>
                  </>
                )}

                {task.completionNotes && (
                  <>
                    <dt className="task-detail__field-label">{t('tasks.detail.completionNotes')}</dt>
                    <dd className="task-detail__field-value">{task.completionNotes}</dd>
                  </>
                )}

                {task.skipReason && (
                  <>
                    <dt className="task-detail__field-label">{t('tasks.detail.skipReason')}</dt>
                    <dd className="task-detail__field-value">{task.skipReason}</dd>
                  </>
                )}
              </dl>
            </section>

            {task.isAiGenerated && (
              <section className="task-detail__section" data-testid="ai-reasoning" aria-labelledby="task-detail-reasoning">
                <Heading level={3} id="task-detail-reasoning" className="task-detail__section-title">
                  {t('tasks.detail.aiReasoningTitle')}
                </Heading>
                {reasoningQuery.isLoading && (
                  <LiveRegion busy>{t('tasks.detail.aiReasoningLoading')}</LiveRegion>
                )}
                {reasoningQuery.isError && !reasoningQuery.isLoading && (
                  <Alert compact tone="warning" live="polite">{t('tasks.detail.aiReasoningError')}</Alert>
                )}
                {!reasoningQuery.isLoading && !reasoningQuery.isError && reasoning && (
                  <div className="task-detail__reasoning">
                    <Text size="sm">{String(reasoning.explanation ?? '')}</Text>
                    {Array.isArray(reasoning.evidence) && (
                      <ul className="task-detail__evidence-list">
                        {reasoning.evidence.map((item) => (
                          <li key={item.id}>{item.label}</li>
                        ))}
                      </ul>
                    )}
                    {typeof reasoning.confidence === 'number' && (
                      <Text size="sm" color="muted">
                        {t('tasks.detail.confidence', { value: Math.round(reasoning.confidence * 100) })}
                      </Text>
                    )}
                  </div>
                )}
              </section>
            )}

            <section className="task-detail__section" data-testid="activity-history" aria-labelledby="task-detail-history">
              <Heading level={3} id="task-detail-history" className="task-detail__section-title">
                {t('tasks.detail.history')}
              </Heading>
              <ul className="task-detail__history">
                <li className="task-detail__history-item">
                  <span>{t('tasks.detail.created')}</span>
                  <time dateTime={task.createdAt} className="task-detail__history-date">{fmt.dateTime(task.createdAt)}</time>
                </li>
                <li className="task-detail__history-item">
                  <span>{t('tasks.detail.updated')}</span>
                  <time dateTime={task.updatedAt} className="task-detail__history-date">{fmt.dateTime(task.updatedAt)}</time>
                </li>
                {task.completedAt && (
                  <li className="task-detail__history-item">
                    <span>{t('tasks.detail.finished')}</span>
                    <time dateTime={task.completedAt} className="task-detail__history-date">{fmt.dateTime(task.completedAt)}</time>
                  </li>
                )}
              </ul>
            </section>
          </div>
        )}
      </Dialog>

      {task && (
        <DeleteTaskDialog
          isOpen={deleteOpen}
          title={task.title}
          isRecurring={recurring}
          isDeleting={isDeleting}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={handleDelete}
        />
      )}

      {task && onEdit && (
        <EditScopeDialog
          isOpen={editScopeOpen}
          onCancel={() => setEditScopeOpen(false)}
          onContinue={(scope) => {
            setEditScopeOpen(false);
            onEdit(task, scope);
          }}
        />
      )}
    </>
  );
}

export default TaskDetail;
